// =============================================================================
// Generic lookup manager — faithful port of POS frmDepartment/frmSubDepartment/
// frmCategory/frmSubCategory dashboards. Two-pane: parent list + child list for
// the selected parent, each with add/rename/active-toggle.
// =============================================================================
import { useEffect, useState, useCallback } from 'react'
import { Plus, Pencil, Check } from 'lucide-react'
import { listLookup, createLookup, updateLookup, type LookupRow } from '../services/data'
import { PageHeader, Card, Spinner, Button } from '../components/shared/ui'
import { toast } from 'react-toastify'

interface Props {
  title: string
  parentTable: string
  parentLabel: string
  childTable: string
  childLabel: string
  childParentCol: string
}

export default function LookupManager({ title, parentTable, parentLabel, childTable, childLabel, childParentCol }: Props) {
  const [parents, setParents] = useState<LookupRow[]>([])
  const [selected, setSelected] = useState<LookupRow | null>(null)
  const [children, setChildren] = useState<LookupRow[]>([])
  const [loading, setLoading] = useState(true)

  const loadParents = useCallback(async () => {
    setLoading(true)
    try {
      const rows = await listLookup(parentTable)
      setParents(rows)
      setSelected(prev => prev ? rows.find(r => r.id === prev.id) ?? rows[0] ?? null : rows[0] ?? null)
    } finally { setLoading(false) }
  }, [parentTable])

  const loadChildren = useCallback(async (parentId: string) => {
    setChildren(await listLookup(childTable, childParentCol, parentId))
  }, [childTable, childParentCol])

  useEffect(() => { loadParents() }, [loadParents])
  useEffect(() => { if (selected) loadChildren(selected.id) }, [selected, loadChildren])

  const addParent = async () => {
    const name = window.prompt(`New ${parentLabel} name:`)?.trim()
    if (!name) return
    try { await createLookup(parentTable, { name, is_active: true }); toast.success('Added'); loadParents() }
    catch { toast.error('Failed to add') }
  }
  const renameParent = async (row: LookupRow) => {
    const name = window.prompt(`Rename ${parentLabel}:`, row.name)?.trim()
    if (!name || name === row.name) return
    try { await updateLookup(parentTable, row.id, { name }); toast.success('Renamed'); loadParents() }
    catch { toast.error('Failed to rename') }
  }
  const toggleParent = async (row: LookupRow) => {
    try { await updateLookup(parentTable, row.id, { is_active: !row.is_active }); loadParents() }
    catch { toast.error('Failed') }
  }

  const addChild = async () => {
    if (!selected) return
    const name = window.prompt(`New ${childLabel} name (under ${selected.name}):`)?.trim()
    if (!name) return
    try { await createLookup(childTable, { name, is_active: true, [childParentCol]: selected.id }); toast.success('Added'); loadChildren(selected.id) }
    catch { toast.error('Failed to add') }
  }
  const renameChild = async (row: LookupRow) => {
    const name = window.prompt(`Rename ${childLabel}:`, row.name)?.trim()
    if (!name || name === row.name) return
    try { await updateLookup(childTable, row.id, { name }); toast.success('Renamed'); if (selected) loadChildren(selected.id) }
    catch { toast.error('Failed to rename') }
  }

  if (loading) return <Spinner label={`Loading ${title.toLowerCase()}…`} />

  return (
    <div>
      <PageHeader title={title} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">{parentLabel}s</h3>
            <Button onClick={addParent}><Plus size={15} /> Add</Button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {parents.map(p => (
              <div key={p.id}
                className={`flex items-center justify-between px-4 py-2.5 border-b border-gray-100 cursor-pointer ${selected?.id === p.id ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                onClick={() => setSelected(p)}>
                <span className={`text-sm ${p.is_active === false ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{p.name}</span>
                <span className="flex items-center gap-2">
                  <button onClick={e => { e.stopPropagation(); renameParent(p) }} className="text-gray-400 hover:text-green-700"><Pencil size={14} /></button>
                  <button onClick={e => { e.stopPropagation(); toggleParent(p) }} className={`text-xs px-2 py-0.5 rounded ${p.is_active === false ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                    {p.is_active === false ? 'Inactive' : <Check size={12} />}
                  </button>
                </span>
              </div>
            ))}
            {parents.length === 0 && <p className="text-center text-gray-400 py-8 text-sm">None yet</p>}
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">{childLabel}s {selected && <span className="text-sm text-gray-400">— {selected.name}</span>}</h3>
            <Button onClick={addChild} disabled={!selected}><Plus size={15} /> Add</Button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {children.map(c => (
              <div key={c.id} className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50">
                <span className={`text-sm ${c.is_active === false ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{c.name}</span>
                <button onClick={() => renameChild(c)} className="text-gray-400 hover:text-green-700"><Pencil size={14} /></button>
              </div>
            ))}
            {selected && children.length === 0 && <p className="text-center text-gray-400 py-8 text-sm">No {childLabel.toLowerCase()}s under {selected.name}</p>}
            {!selected && <p className="text-center text-gray-400 py-8 text-sm">Select a {parentLabel.toLowerCase()}</p>}
          </div>
        </Card>
      </div>
    </div>
  )
}
